/**
 * 통합 메모 API 클라이언트
 * V152: 중앙 집중식 메모 관리
 */

import apiClient from './client';
import type { MemoDto, MemoEntityType, CreateMemoRequest, UpdateMemoRequest } from '@/types/memo';

export const memoApi = {
  /**
   * 특정 엔티티의 메모 목록 조회
   */
  getMemosByEntity: async (entityType: MemoEntityType, entityId: number): Promise<MemoDto[]> => {
    const response = await apiClient.get<MemoDto[]>('/api/memos', {
      params: { entityType, entityId },
    });
    return response.data;
  },

  /**
   * 고객 메모 조회 (편의 메서드)
   */
  getCustomerMemos: async (customerId: number): Promise<MemoDto[]> => {
    const response = await apiClient.get<MemoDto[]>(`/api/memos/customer/${customerId}`);
    return response.data;
  },

  /**
   * 상담 메모 조회 (편의 메서드)
   */
  getConsultMemos: async (consultId: number): Promise<MemoDto[]> => {
    const response = await apiClient.get<MemoDto[]>(`/api/memos/consult/${consultId}`);
    return response.data;
  },

  /**
   * 예약 메모 조회 (편의 메서드)
   */
  getAppointmentMemos: async (appointmentId: number): Promise<MemoDto[]> => {
    const response = await apiClient.get<MemoDto[]>(`/api/memos/appointment/${appointmentId}`);
    return response.data;
  },

  /**
   * 메모 생성
   */
  createMemo: async (request: CreateMemoRequest): Promise<MemoDto> => {
    const response = await apiClient.post<MemoDto>('/api/memos', request);
    return response.data;
  },

  /**
   * 고객 메모 추가 (편의 메서드)
   */
  addCustomerMemo: async (customerId: number, content: string): Promise<MemoDto> => {
    const response = await apiClient.post<MemoDto>(`/api/memos/customer/${customerId}`, content, {
      headers: { 'Content-Type': 'text/plain' },
    });
    return response.data;
  },

  /**
   * 상담 메모 추가 (편의 메서드)
   */
  addConsultMemo: async (consultId: number, content: string): Promise<MemoDto> => {
    const response = await apiClient.post<MemoDto>(`/api/memos/consult/${consultId}`, content, {
      headers: { 'Content-Type': 'text/plain' },
    });
    return response.data;
  },

  /**
   * 예약 메모 추가 (편의 메서드)
   */
  addAppointmentMemo: async (appointmentId: number, content: string): Promise<MemoDto> => {
    const response = await apiClient.post<MemoDto>(`/api/memos/appointment/${appointmentId}`, content, {
      headers: { 'Content-Type': 'text/plain' },
    });
    return response.data;
  },

  /**
   * 메모 수정
   */
  updateMemo: async (memoId: number, request: UpdateMemoRequest): Promise<MemoDto> => {
    const response = await apiClient.put<MemoDto>(`/api/memos/${memoId}`, request);
    return response.data;
  },

  /**
   * 메모 삭제
   */
  deleteMemo: async (memoId: number): Promise<void> => {
    await apiClient.delete(`/api/memos/${memoId}`);
  },
};

export default memoApi;
